import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import {
  ACCESS_TIERS,
  VIDEO_CATEGORIES,
  VIDEO_STATUSES,
} from "@demore/shared/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/input";
import { api } from "@/lib/api";
import { cn, formatDuration } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { canUploadVideo } from "@/lib/permissions";

type VideoRow = {
  id: string;
  title: string;
  description: string | null;
  category: string;
  accessTier: string;
  status: string;
  campaignId: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  // Audit V1
  categoryTag?: string | null;
  source?: "mux" | "youtube";
};

type Campaign = { id: string; name: string };

export function LibraryPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    q: "",
    campaignId: "",
    category: "",
    status: "",
    accessTier: "",
    favoritesOnly: false,
    promoted: false,
    categoryTag: "",
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [youtubeOpen, setYoutubeOpen] = useState(false);

  const { data: campaignData } = useQuery({
    queryKey: ["campaigns-mini"],
    queryFn: () => api.get<{ items: Campaign[] }>("/api/admin/campaigns"),
  });
  const campaigns = campaignData?.items ?? [];

  const { data, isLoading, error } = useQuery({
    queryKey: ["library", filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.campaignId) params.set("campaignId", filters.campaignId);
      if (filters.category) params.set("category", filters.category);
      if (filters.status) params.set("status", filters.status);
      if (filters.accessTier) params.set("accessTier", filters.accessTier);
      if (filters.favoritesOnly) params.set("favoritesOnly", "true");
      if (filters.promoted) params.set("promoted", "true");
      if (filters.categoryTag) params.set("categoryTag", filters.categoryTag);
      return api.get<{ items: VideoRow[] }>(`/api/admin/videos?${params}`);
    },
  });

  // Audit V1: harvest distinct categoryTag values from the current result for
  // quick-filter chips. (Cheap; full list lives server-side but the chip set
  // can come from what's on screen.)
  const tagChips = Array.from(
    new Set(
      (data?.items ?? [])
        .map((v) => (v as VideoRow).categoryTag)
        .filter((t): t is string => Boolean(t)),
    ),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">Library</h1>
        {canUploadVideo(user?.role) && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setYoutubeOpen(true)}>
              Add YouTube video
            </Button>
            <Button onClick={() => setCreateOpen(true)}>Upload video</Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-6">
          <Input
            placeholder="Search title or description"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          <Select value={filters.campaignId} onChange={(e) => setFilters((f) => ({ ...f, campaignId: e.target.value }))}>
            <option value="">All campaigns</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
            <option value="">All categories</option>
            {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </Select>
          <Select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Any status</option>
            {VIDEO_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </Select>
          <Select value={filters.accessTier} onChange={(e) => setFilters((f) => ({ ...f, accessTier: e.target.value }))}>
            <option value="">Any tier</option>
            {ACCESS_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </Select>
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={filters.favoritesOnly} onChange={(e) => setFilters((f) => ({ ...f, favoritesOnly: e.target.checked }))} />
              Favorites
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={filters.promoted} onChange={(e) => setFilters((f) => ({ ...f, promoted: e.target.checked }))} />
              Promoted
            </label>
          </div>
        </div>
        {/* Audit V1: category-tag chip filter */}
        {(tagChips.length > 0 || filters.categoryTag) && (
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground">Tag:</span>
            {filters.categoryTag && (
              <button
                onClick={() => setFilters((f) => ({ ...f, categoryTag: "" }))}
                className="px-2 py-0.5 text-xs rounded-md border border-gold bg-gold/15"
              >
                {filters.categoryTag} ×
              </button>
            )}
            {tagChips
              .filter((t) => t !== filters.categoryTag)
              .map((t) => (
                <button
                  key={t}
                  onClick={() => setFilters((f) => ({ ...f, categoryTag: t }))}
                  className={cn(
                    "px-2 py-0.5 text-xs rounded-md border border-admin-rule hover:bg-foreground/5",
                  )}
                >
                  {t}
                </button>
              ))}
          </div>
        )}
      </Card>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {/* A failed list request used to render as "Your library is empty" —
          which read as vanished uploads (QA defect 2502). Fail loudly. */}
      {!isLoading && error && (
        <Card>
          <div className="py-8 px-6 text-center space-y-2">
            <div className="font-head text-lg">Couldn't load the library</div>
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Request failed"}
            </p>
          </div>
        </Card>
      )}

      {!isLoading && !error && (data?.items ?? []).length === 0 && (
        <Card>
          <div className="py-12 px-6 text-center space-y-3">
            <div className="font-head text-lg">Your library is empty</div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {filters.q || filters.category || filters.status || filters.accessTier || filters.favoritesOnly || filters.promoted
                ? "No videos match the active filters. Clear them or try a different search."
                : "Upload your first video to get started. Once it's approved you can drop it into a playlist and send a personalised Demo Pack."}
            </p>
            {canUploadVideo(user?.role) &&
              !(filters.q || filters.category || filters.status || filters.accessTier || filters.favoritesOnly || filters.promoted) && (
              <Button onClick={() => setCreateOpen(true)}>Upload your first video</Button>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {(data?.items ?? []).map((v) => (
          <Link key={v.id} to={`/admin/library/${v.id}`}>
            <Card className="overflow-hidden h-full hover:border-gold transition-colors">
              <div className="aspect-video bg-navy-700 relative">
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No thumbnail</div>
                )}
                <Badge className="absolute top-2 left-2" variant={v.status === "approved" ? "ok" : v.status === "pending_approval" ? "default" : "muted"}>
                  {v.status.replace(/_/g, " ")}
                </Badge>
                {v.durationSeconds && (
                  <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-1.5 py-0.5 rounded">
                    {formatDuration(v.durationSeconds)}
                  </span>
                )}
              </div>
              <div className="p-3 space-y-1">
                <div className="font-medium text-sm line-clamp-1">{v.title}</div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline">{v.category.replace(/_/g, " ")}</Badge>
                  <Badge variant="muted">{v.accessTier.replace(/_/g, " ")}</Badge>
                  {v.categoryTag && <Badge variant="outline">{v.categoryTag}</Badge>}
                  {v.source === "youtube" && <Badge variant="outline">YouTube</Badge>}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <CreateVideoDialog open={createOpen} onOpenChange={setCreateOpen} campaigns={campaigns} onCreated={() => qc.invalidateQueries({ queryKey: ["library"] })} />
      <YoutubeVideoDialog open={youtubeOpen} onOpenChange={setYoutubeOpen} campaigns={campaigns} onCreated={() => qc.invalidateQueries({ queryKey: ["library"] })} />
    </div>
  );
}

// Audit V1: Add a YouTube-source video instead of uploading. Exported so the
// workspace Media tab can reuse it with the entered campaign locked in
// (post-cutover, the workspace is the only upload surface).
export function YoutubeVideoDialog({
  open,
  onOpenChange,
  onCreated,
  campaigns,
  initialCampaignId = "",
  lockCampaign = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  campaigns: Campaign[];
  initialCampaignId?: string;
  lockCampaign?: boolean;
}) {
  const [form, setForm] = useState({
    youtubeUrl: "",
    title: "",
    description: "",
    category: "corporate_intro",
    // CVO review #2: YouTube is Public-tier only (IFrame embeds can't be
    // tier/NDA-gated and capture no engagement analytics).
    accessTier: "public",
    categoryTag: "",
    campaignId: initialCampaignId,
  });
  useEffect(() => {
    if (open && lockCampaign) {
      setForm((f) => ({ ...f, campaignId: initialCampaignId }));
    }
  }, [initialCampaignId, lockCampaign, open]);
  const create = useMutation({
    mutationFn: () =>
      api.post("/api/admin/videos/youtube", {
        youtubeUrl: form.youtubeUrl,
        title: form.title,
        description: form.description || undefined,
        category: form.category,
        accessTier: form.accessTier,
        categoryTag: form.categoryTag || null,
        campaignId: form.campaignId || null,
      }),
    onSuccess: () => {
      toast.success("YouTube video added");
      onCreated();
      onOpenChange(false);
      setForm({
        youtubeUrl: "",
        title: "",
        description: "",
        category: "corporate_intro",
        accessTier: "public",
        categoryTag: "",
        campaignId: initialCampaignId,
      });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  // Audit V1: oEmbed pre-fill -- when the URL field loses focus, fetch the
  // canonical title from YouTube and pop it into the title input if the
  // admin hasn't already typed one. Silently ignores errors.
  async function preview() {
    if (!form.youtubeUrl) return;
    try {
      const resp = await api.get<{
        title: string;
        authorName: string;
        thumbnailUrl: string;
      }>(`/api/admin/videos/youtube/preview?url=${encodeURIComponent(form.youtubeUrl)}`);
      if (resp.title && !form.title) {
        setForm((f) => ({ ...f, title: resp.title }));
      }
    } catch {
      // Swallow -- the admin can fill the title manually.
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a YouTube video</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>YouTube URL</Label>
              <Input
                value={form.youtubeUrl}
                onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                onBlur={() => preview()}
                placeholder="https://www.youtube.com/watch?v=…"
              />
              <p className="text-[11px] text-muted-foreground">
                We'll auto-fill the title from YouTube when you tab away.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Access tier</Label>
                <Select value="public" disabled>
                  <option value="public">public</option>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  YouTube content is Public-tier only — IFrame embeds can't be
                  tier/NDA-gated. Use a Mux upload for gated content.
                </p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Campaign {lockCampaign ? "" : "(optional)"}</Label>
              <Select value={form.campaignId} onChange={(e) => setForm((f) => ({ ...f, campaignId: e.target.value }))} disabled={lockCampaign}>
                <option value="">No campaign</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category tag (optional, free text e.g. "Frylow")</Label>
              <Input
                value={form.categoryTag}
                onChange={(e) => setForm((f) => ({ ...f, categoryTag: e.target.value }))}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              YouTube videos play in an embedded IFrame. Heartbeats / milestones
              are not captured for them in V1, so engagement analytics will read
              0% completion.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!form.youtubeUrl || !form.title || create.isPending || (form.accessTier === "campaign_assigned" && !form.campaignId)}
          >
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Exported so the workspace Media tab can reuse it with the entered campaign
// locked in (post-cutover, the workspace is the only upload surface).
export function CreateVideoDialog({
  open,
  onOpenChange,
  onCreated,
  campaigns,
  initialCampaignId = "",
  lockCampaign = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  campaigns: Campaign[];
  initialCampaignId?: string;
  lockCampaign?: boolean;
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "corporate_intro",
    accessTier: "registered",
    categoryTag: "",
    campaignId: initialCampaignId,
  });
  useEffect(() => {
    if (open && lockCampaign) {
      setForm((f) => ({ ...f, campaignId: initialCampaignId }));
    }
  }, [initialCampaignId, lockCampaign, open]);
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [stage, setStage] = useState<"idle" | "creating" | "uploading" | "processing">("idle");

  function pickFile(f: File | null) {
    setFile(f);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(f ? URL.createObjectURL(f) : null);
  }

  function reset() {
    setForm({ title: "", description: "", category: "corporate_intro", accessTier: "registered", categoryTag: "", campaignId: initialCampaignId });
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(null);
    setFilePreview(null);
    setProgress(null);
    setStage("idle");
  }

  async function uploadWithProgress(url: string, blob: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)));
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(blob);
    });
  }

  async function submit() {
    try {
      setStage("creating");
      const video = await api.post<{ id: string }>("/api/admin/videos", {
        title: form.title,
        description: form.description,
        category: form.category,
        accessTier: form.accessTier,
        categoryTag: form.categoryTag || null,
        campaignId: form.campaignId || null,
      });
      const upload = await api.post<{ uploadUrl: string; uploadId: string; versionId: string }>(
        "/api/admin/videos/mux-upload",
        { videoId: video.id },
      );
      if (file && !upload.uploadUrl.startsWith("data:")) {
        // Real Mux upload — PUT the file with progress.
        setStage("uploading");
        setProgress(0);
        await uploadWithProgress(upload.uploadUrl, file);
      }
      setStage("processing");
      toast.success(file ? "Uploaded — Mux is transcoding" : "Video created — upload pending");
      onCreated();
      onOpenChange(false);
      reset();
    } catch (err) {
      setStage("idle");
      setProgress(null);
      toast.error(err instanceof Error ? err.message : "Create failed");
    }
  }

  const isStub = (uploadUrl: string) => uploadUrl.startsWith("data:");
  const busy = stage !== "idle";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload a video</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} disabled={busy} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={busy} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} disabled={busy}>
                  {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Access tier</Label>
                <Select value={form.accessTier} onChange={(e) => setForm((f) => ({ ...f, accessTier: e.target.value }))} disabled={busy}>
                  {ACCESS_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Campaign {form.accessTier === "campaign_assigned" ? "(required for campaign-assigned tier)" : lockCampaign ? "" : "(optional)"}</Label>
              <Select value={form.campaignId} onChange={(e) => setForm((f) => ({ ...f, campaignId: e.target.value }))} disabled={busy || lockCampaign}>
                <option value="">No campaign</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              {form.accessTier === "campaign_assigned" && !form.campaignId && (
                <p className="text-[11px] text-destructive">
                  Campaign-assigned videos unlock via campaign membership — pick the campaign or the video can never play.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Category tag (optional, e.g. "Frylow")</Label>
              <Input
                value={form.categoryTag}
                onChange={(e) => setForm((f) => ({ ...f, categoryTag: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Video file</Label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                disabled={busy}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:rounded-md file:bg-gold file:text-navy file:font-semibold hover:file:bg-gold-600"
              />
              {filePreview ? (
                <video
                  src={filePreview}
                  controls
                  className="w-full max-w-sm rounded-md border border-admin-rule bg-black"
                  preload="metadata"
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Pick a video file to preview it instantly. We'll mint a Mux direct-upload URL and PUT the file
                  on submit — Mux transcodes in 30s–2min, then the real thumbnail appears in the library.
                </p>
              )}
            </div>
            {progress !== null && (
              <div className="space-y-1">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {stage === "uploading" ? `Uploading ${progress}%` : "Processing…"}
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.title || (form.accessTier === "campaign_assigned" && !form.campaignId)}>
            {stage === "creating" ? "Creating…" : stage === "uploading" ? "Uploading…" : stage === "processing" ? "Processing…" : "Create + upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
